import { useEffect, useMemo, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash, CircleNotch, CalendarCheck, Clock, User, CaretLeft, CaretRight, ListBullets, CalendarBlank } from "@phosphor-icons/react";

const STATUSES = ["Scheduled", "Completed", "Cancelled"];
const STATUS_STYLE = {
  Scheduled: { bg: "var(--bk-accent-glow)", color: "var(--bk-accent)" },
  Completed: { bg: "var(--bk-success-bg)", color: "var(--bk-success)" },
  Cancelled: { bg: "#FDECE8", color: "#B23B1F" },
  "No-show": { bg: "#FFF3DC", color: "#B08840" },
};

const EMPTY = { equipment: "", date: "", start_time: "", end_time: "", booked_by: "", label: "", status: "Scheduled", notes: "" };

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }
  catch { return d; }
};

export function EquipmentScheduler({ businessId, wellness = false }) {
  const [bookings, setBookings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [ctx, setCtx] = useState(null);
  const [serviceId, setServiceId] = useState("");
  const [creditId, setCreditId] = useState("");
  const [noShowFor, setNoShowFor] = useState(null);
  const [nsForm, setNsForm] = useState({ fee_action: "waive", fee_amount: "", credit_action: "restore" });
  const [nsBusy, setNsBusy] = useState(false);

  const load = () => api.get(`/businesses/${businessId}/schedules`).then((r) => setBookings(r.data)).catch(() => {});
  const loadCtx = () => wellness && api.get(`/businesses/${businessId}/wellness/booking-context`).then((r) => setCtx(r.data)).catch(() => {});
  useEffect(() => { load(); loadCtx(); /* eslint-disable-next-line */ }, [businessId, wellness]);

  const selectedService = ctx?.services?.find((s) => s.id === serviceId);
  const eligibleClients = (ctx?.clients || []).filter((c) =>
    !selectedService?.equipment || !c.equipment || c.equipment.toLowerCase() === selectedService.equipment.toLowerCase());
  const selectedCredit = ctx?.clients?.find((c) => c.credit_id === creditId);

  const create = async () => {
    if (wellness && (serviceId || creditId)) {
      if (!form.date) return toast.error("Pick a date");
      setSaving(true);
      try {
        const { data } = await api.post(`/businesses/${businessId}/wellness/bookings`, {
          date: form.date, start_time: form.start_time, end_time: form.end_time,
          service_id: serviceId || null, credit_id: creditId || null,
          client_name: form.booked_by, notes: form.notes,
        });
        toast.success(data.rowan_message || "Booking added");
        if (form.date) setWeekStart(startOfWeek(new Date(`${form.date}T12:00:00`)));
        setForm(EMPTY); setServiceId(""); setCreditId(""); setShowForm(false);
        load(); loadCtx();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Couldn't add booking.");
      } finally { setSaving(false); }
      return;
    }
    if (!form.equipment.trim()) return toast.error("Add an equipment or room name");
    if (!form.date) return toast.error("Pick a date");
    setSaving(true);
    try {
      await api.post(`/businesses/${businessId}/schedules`, form);
      toast.success("Booking added");
      if (form.date) setWeekStart(startOfWeek(new Date(`${form.date}T12:00:00`)));
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add booking.");
    } finally { setSaving(false); }
  };

  const markNoShow = async () => {
    setNsBusy(true);
    try {
      const payload = { fee_action: nsForm.fee_action, credit_action: nsForm.credit_action };
      if (nsForm.fee_action === "charge" && nsForm.fee_amount !== "") payload.fee_amount = Number(nsForm.fee_amount);
      const { data } = await api.post(`/businesses/${businessId}/wellness/bookings/${noShowFor.id}/no-show`, payload);
      toast.success(data.rowan_message);
      setNoShowFor(null); load(); loadCtx();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't record the missed visit."); }
    finally { setNsBusy(false); }
  };

  const undoNoShow = async (b) => {
    try {
      const { data } = await api.post(`/businesses/${businessId}/wellness/bookings/${b.id}/no-show/undo`);
      toast.success(data.rowan_message);
      load(); loadCtx();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't undo that."); }
  };

  const setStatus = async (b, status) => {
    if (wellness && status === "No-show") {
      setNsForm({ fee_action: "waive", fee_amount: "", credit_action: "restore" });
      setNoShowFor(b);
      return;
    }
    try {
      if (wellness && b.credit_id && (status === "Completed" || status === "Cancelled")) {
        const action = status === "Completed" ? "complete" : "cancel";
        const { data } = await api.post(`/businesses/${businessId}/wellness/bookings/${b.id}/${action}`);
        load(); loadCtx();
        if (status === "Cancelled") {
          toast.success(data.rowan_message, { duration: 8000, action: { label: "Undo", onClick: async () => {
            try { const r = await api.post(`/businesses/${businessId}/wellness/bookings/${b.id}/cancel/undo`); toast.success(r.data.rowan_message); load(); loadCtx(); }
            catch (err) { toast.error(err.response?.data?.detail || "Couldn't undo that cancellation."); }
          } } });
        } else toast.success(data.rowan_message);
        return;
      }
      const prior = b.status;
      const { data } = await api.patch(`/schedules/${b.id}`, { status });
      setBookings((xs) => xs.map((x) => (x.id === b.id ? data : x)));
      if (status === "Cancelled") {
        toast.success("Booking cancelled.", { duration: 8000, action: { label: "Undo", onClick: async () => {
          try { const r = await api.patch(`/schedules/${b.id}`, { status: prior || "Scheduled" }); setBookings((xs) => xs.map((x) => (x.id === b.id ? r.data : x))); toast.success(`Undone — back to ${prior || "Scheduled"}.`); }
          catch { toast.error("Couldn't undo that cancellation."); }
        } } });
      }
    } catch { toast.error("Couldn't update booking."); }
  };

  const del = async (id) => {
    try { await api.delete(`/schedules/${id}`); setBookings((x) => x.filter((b) => b.id !== id)); } catch {}
  };

  const openNewOn = (iso) => { setForm({ ...EMPTY, date: iso }); setShowForm(true); };

  const weekDays = useMemo(() => {
    return [...Array(7)].map((_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const byDate = useMemo(() => {
    const map = {};
    for (const b of bookings) { (map[b.date] = map[b.date] || []).push(b); }
    for (const k in map) map[k].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    return map;
  }, [bookings]);

  const todayISO = toISO(new Date());
  const weekLabel = `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  const shiftWeek = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setWeekStart(d); };

  return (
    <div className="space-y-6" data-testid="scheduler-panel">
      <div className="bk-card p-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <CalendarCheck size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Equipment scheduler</h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Book rooms, equipment, and treatment slots. No patient or health details are stored here.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full p-0.5 border" style={{ borderColor: "var(--bk-border)" }} data-testid="scheduler-view-toggle">
            {[{ k: "week", label: "Week", icon: CalendarBlank }, { k: "list", label: "List", icon: ListBullets }].map((v) => (
              <button key={v.k} onClick={() => setView(v.k)} data-testid={`scheduler-view-${v.k}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors" style={view === v.k ? { background: "var(--bk-accent)", color: "#fff" } : { color: "var(--bk-text-2)" }}>
                <v.icon size={14} weight="duotone" /> {v.label}
              </button>
            ))}
          </div>
          <button onClick={() => { setForm(EMPTY); setShowForm((s) => !s); }} data-testid="new-booking-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold shrink-0">
            <Plus size={16} weight="bold" /> New booking
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bk-card p-6 space-y-3" data-testid="booking-form">
          {wellness && ctx && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 border-b" style={{ borderColor: "var(--bk-border)" }} data-testid="wellness-booking-fields">
              <div>
                <select value={serviceId} onChange={(e) => { setServiceId(e.target.value); setCreditId(""); }} data-testid="booking-service" className="w-full rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
                  <option value="">Service (from your menu, optional)</option>
                  {ctx.services.map((s) => <option key={s.id} value={s.id}>{s.name} — ${Number(s.price).toLocaleString()}{s.duration_min ? ` · ${s.duration_min} min` : ""}</option>)}
                </select>
                {selectedService?.equipment && <p className="text-[11px] mt-1" style={{ color: "var(--bk-text-2)" }}>Uses: {selectedService.equipment}</p>}
              </div>
              <div>
                <select value={creditId} onChange={(e) => setCreditId(e.target.value)} data-testid="booking-member" className="w-full rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
                  <option value="">Member / package holder (optional)</option>
                  {eligibleClients.map((c) => <option key={c.credit_id} value={c.credit_id}>{c.client_name} — {c.item_name} ({c.sessions_remaining} left)</option>)}
                </select>
                {selectedCredit && (
                  <p className="text-[11px] mt-1 font-semibold" style={{ color: "var(--bk-success)" }} data-testid="booking-member-remaining">
                    {selectedCredit.sessions_remaining} session{selectedCredit.sessions_remaining !== 1 ? "s" : ""} remaining — 1 is deducted when the booking is marked Completed
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!(wellness && serviceId) && <input placeholder="Equipment / room (e.g. Massage Room A)" value={form.equipment} onChange={(e) => setForm((f) => ({ ...f, equipment: e.target.value }))} data-testid="booking-equipment" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />}
            <input placeholder="Location label (optional, e.g. Suite 2)" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} data-testid="booking-label" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <DateInput value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="booking-date" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Booked by (optional)" value={form.booked_by} onChange={(e) => setForm((f) => ({ ...f, booked_by: e.target.value }))} data-testid="booking-booked-by" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} data-testid="booking-start" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} data-testid="booking-end" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="booking-status" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="booking-notes" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          </div>
          <div className="flex justify-end">
            <button onClick={create} disabled={saving} data-testid="save-booking-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-70">
              {saving ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : <>Add booking</>}
            </button>
          </div>
        </div>
      )}

      {view === "week" ? (
        <div className="bk-card p-4 sm:p-5" data-testid="scheduler-week">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => shiftWeek(-1)} data-testid="week-prev" className="p-2 rounded-full border hover:bg-slate-100" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} aria-label="Previous week"><CaretLeft size={16} weight="bold" /></button>
            <div className="text-center">
              <p className="font-display font-bold text-base" style={{ color: "var(--bk-text)" }} data-testid="week-label">{weekLabel}</p>
              <button onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="week-today" className="text-xs font-semibold hover:underline" style={{ color: "var(--bk-accent)" }}>This week</button>
            </div>
            <button onClick={() => shiftWeek(1)} data-testid="week-next" className="p-2 rounded-full border hover:bg-slate-100" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} aria-label="Next week"><CaretRight size={16} weight="bold" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
            {weekDays.map((d) => {
              const iso = toISO(d);
              const items = (byDate[iso] || []).filter((b) => b.status !== "Cancelled");
              const isToday = iso === todayISO;
              return (
                <div key={iso} data-testid={`week-day-${iso}`} className="rounded-lg border min-h-[120px] flex flex-col" style={{ borderColor: isToday ? "var(--bk-accent)" : "var(--bk-border)", background: isToday ? "var(--bk-accent-glow)" : "var(--bk-surface)" }}>
                  <div className="px-2.5 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--bk-border)" }}>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide font-bold" style={{ color: isToday ? "var(--bk-accent)" : "var(--bk-text-2)" }}>{d.toLocaleDateString("en-US", { weekday: "short" })}</p>
                      <p className="text-sm font-bold" style={{ color: "var(--bk-text)" }}>{d.getDate()}</p>
                    </div>
                    <button onClick={() => openNewOn(iso)} data-testid={`week-add-${iso}`} className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/5" style={{ color: "var(--bk-accent)" }} aria-label="Add booking"><Plus size={14} weight="bold" /></button>
                  </div>
                  <div className="p-1.5 space-y-1.5 flex-1">
                    {items.length === 0 ? (
                      <p className="text-[11px] px-1 py-2" style={{ color: "var(--bk-disabled)" }}>No bookings</p>
                    ) : items.map((b) => {
                      const ss = STATUS_STYLE[b.status] || STATUS_STYLE.Scheduled;
                      return (
                        <div key={b.id} data-testid={`week-booking-${b.id}`} className="rounded-md px-2 py-1.5 text-[11px] leading-tight" style={{ background: ss.bg, color: ss.color }}>
                          {(b.start_time || b.end_time) && <span className="font-mono font-semibold block">{b.start_time || "—"}{b.end_time ? `–${b.end_time}` : ""}</span>}
                          <span className="font-semibold block truncate" style={{ color: "var(--bk-text)" }}>{b.equipment}</span>
                          {b.booked_by && <span className="block truncate" style={{ color: "var(--bk-text-2)" }}>{b.booked_by}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : bookings.length === 0 ? (
        <div className="bk-card p-10 text-center">
          <CalendarCheck size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>No bookings yet</p>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Add your first equipment or room booking to keep your schedule organized.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {bookings.map((b) => {
            const ss = STATUS_STYLE[b.status] || STATUS_STYLE.Scheduled;
            return (
              <div key={b.id} data-testid={`booking-${b.id}`} className="bk-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-bold truncate" style={{ color: "var(--bk-text)" }}>{b.equipment}</p>
                    {b.label && <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>{b.label}</p>}
                  </div>
                  <select value={b.status} onChange={(e) => setStatus(b, e.target.value)} data-testid={`booking-status-${b.id}`} className="text-xs font-bold px-2 py-1 rounded-full border outline-none shrink-0" style={{ borderColor: "var(--bk-border)", background: ss.bg, color: ss.color }}>
                    {(wellness ? [...STATUSES, "No-show"] : STATUSES).map((s) => <option key={s} value={s}>{s === "No-show" ? "No-show (missed visit)" : s}</option>)}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm" style={{ color: "var(--bk-text-2)" }}>
                  <span className="inline-flex items-center gap-1.5"><CalendarCheck size={15} weight="duotone" /> {fmtDate(b.date)}</span>
                  {(b.start_time || b.end_time) && (
                    <span className="inline-flex items-center gap-1.5" data-testid={`booking-time-${b.id}`}><Clock size={15} weight="duotone" /> {b.start_time || "—"}{b.end_time ? ` – ${b.end_time}` : ""}</span>
                  )}
                  {b.booked_by && <span className="inline-flex items-center gap-1.5"><User size={15} weight="duotone" /> {b.booked_by}</span>}
                </div>
                {b.notes && <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>{b.notes}</p>}
                {b.status === "No-show" && (
                  <div className="flex flex-wrap items-center gap-2 mt-3 rounded-lg px-3 py-2" style={{ background: "#FFF9F0" }} data-testid={`booking-noshow-info-${b.id}`}>
                    <span className="text-xs font-semibold" style={{ color: "#B08840" }}>
                      Missed visit{b.no_show?.fee_action === "charge" ? " · fee charged" : " · fee waived"}
                      {b.credit_id ? (b.no_show?.credit_action === "forfeit" ? " · session forfeited" : " · session kept on package") : ""}
                    </span>
                    <button onClick={() => undoNoShow(b)} data-testid={`booking-undo-noshow-${b.id}`} className="text-xs font-bold hover:underline" style={{ color: "var(--bk-accent)" }}>Undo</button>
                  </div>
                )}
                <div className="flex justify-end mt-3">
                  <button onClick={() => del(b.id)} data-testid={`booking-delete-${b.id}`} className="p-1.5 rounded hover:bg-black/5" style={{ color: "#B23B1F" }} aria-label="Delete"><Trash size={15} weight="bold" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {noShowFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.45)" }} data-testid="noshow-dialog">
          <div className="bk-card p-6 w-full max-w-md space-y-4" style={{ background: "#fff" }}>
            <div>
              <p className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Missed visit — {noShowFor.booked_by || noShowFor.equipment}</p>
              <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
                {fmtDate(noShowFor.date)}{noShowFor.start_time ? ` at ${noShowFor.start_time}` : ""} · {noShowFor.label || noShowFor.equipment}. Nothing is charged or forfeited unless you choose it — and you can undo this any time.
              </p>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--bk-text-2)" }}>Missed-visit fee</p>
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--bk-text)" }}>
                <input type="radio" name="ns-fee" checked={nsForm.fee_action === "waive"} onChange={() => setNsForm((f) => ({ ...f, fee_action: "waive" }))} data-testid="noshow-fee-waive" /> Waive it — no charge
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--bk-text)" }}>
                <input type="radio" name="ns-fee" checked={nsForm.fee_action === "charge"} onChange={() => setNsForm((f) => ({ ...f, fee_action: "charge" }))} data-testid="noshow-fee-charge" /> Charge a fee
              </label>
              {nsForm.fee_action === "charge" && (
                <input type="number" min="0" step="0.01" placeholder="Amount (blank = service price)" value={nsForm.fee_amount}
                  onChange={(e) => setNsForm((f) => ({ ...f, fee_amount: e.target.value }))} data-testid="noshow-fee-amount"
                  className="w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              )}
            </div>
            {noShowFor.credit_id && (
              <div className="space-y-1.5">
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--bk-text-2)" }}>Their package session</p>
                <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--bk-text)" }}>
                  <input type="radio" name="ns-credit" checked={nsForm.credit_action === "restore"} onChange={() => setNsForm((f) => ({ ...f, credit_action: "restore" }))} data-testid="noshow-credit-restore" /> Keep the session on their package
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--bk-text)" }}>
                  <input type="radio" name="ns-credit" checked={nsForm.credit_action === "forfeit"} onChange={() => setNsForm((f) => ({ ...f, credit_action: "forfeit" }))} data-testid="noshow-credit-forfeit" /> They forfeit the session
                </label>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setNoShowFor(null)} data-testid="noshow-cancel-btn" className="px-4 py-2 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>Cancel</button>
              <button onClick={markNoShow} disabled={nsBusy} data-testid="noshow-confirm-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-70">
                {nsBusy ? <CircleNotch size={15} className="animate-spin" /> : null} Record missed visit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
